import { useEffect, useRef } from "react"
import { flushSync } from "react-dom"
import { createRoot, type Root } from "react-dom/client"
import { AlertTriangle } from "lucide-react"

/**
 * Portal-eigene Rueckfrage „Trotzdem …?" — Ersatz fuer `window.confirm()`.
 *
 * 🔴 Anlass 19.10.2026: Robin konnte keine Aufgaben aus Mails mehr anlegen.
 * Am Server kam keine einzige Anfrage an, der Klickweg endete vor dem
 * Absenden. Ursache: Chrome bietet nach ein paar Popups „Diese Seite soll
 * keine weiteren Dialoge anzeigen" an — wer das anhakt, bekommt von
 * `window.confirm()` danach SOFORT `false`, ohne dass etwas erscheint. Der
 * Knopf „Aufgabe erstellen" tat dann sichtbar nichts. Dieser Dialog gehoert
 * dem Portal, keine Browser-Einstellung kann ihn wegschalten.
 *
 * Bonus wie beim Ueberbuchen-Dialog in `InboxActionPlan`: blockiert weder den
 * Tab noch das Chrome DevTools Protocol, Tests klicken ihn wie jeden Knopf.
 *
 * Aufruf von ueberall, auch ausserhalb von React (`lib/aufgabeVerknuepfung`):
 *
 *     if (!(await nachfragen({ titel, zeilen, ja: "Trotzdem anlegen" }))) return
 *
 * Eigene React-Wurzel statt Provider im App-Root: die Anlege-Dialoge werden
 * auch einzeln gerendert (Tests, minimierte Entwuerfe) — ein fehlender
 * Provider haette die Frage nie beantwortet und den Knopf wieder stumm
 * gemacht, also genau den Fehler, um den es hier geht.
 */
export type Rueckfrage = {
  titel: string
  /** Absaetze bzw. Aufzaehlungspunkte unter dem Titel. */
  zeilen: string[]
  /** Beschriftung des Bestaetigen-Knopfs, z. B. „Trotzdem anlegen". */
  ja: string
  nein?: string
}

let offen: { wurzel: Root; knoten: HTMLElement; antworten: (ok: boolean) => void } | null = null

function schliessen(ok: boolean) {
  const o = offen
  if (!o) return
  offen = null
  o.antworten(ok)
  // Nicht mitten im Klick-Handler der eigenen Wurzel abbauen.
  queueMicrotask(() => { o.wurzel.unmount(); o.knoten.remove() })
}

/** Zeigt die Rueckfrage und liefert `true` bei „Ja", sonst `false`.
 *  Eine zweite Frage, waehrend eine offen ist (Doppelklick), beantwortet die
 *  erste mit „Nein" und ersetzt sie — es steht nie mehr als eine da. */
export function nachfragen(frage: Rueckfrage): Promise<boolean> {
  schliessen(false)
  return new Promise<boolean>((antworten) => {
    const knoten = document.createElement("div")
    knoten.setAttribute("data-portal-rueckfrage", "")
    document.body.appendChild(knoten)
    const wurzel = createRoot(knoten)
    offen = { wurzel, knoten, antworten }
    // Synchron, damit die Frage im selben Takt steht wie der Klick.
    flushSync(() => wurzel.render(<RueckfrageDialog frage={frage} onAntwort={schliessen} />))
  })
}

/** Fuer Tests: offene Frage verwerfen (`test-setup.ts` raeumt damit auf). */
export function rueckfrageVerwerfen() {
  schliessen(false)
}

function RueckfrageDialog({ frage, onAntwort }: { frage: Rueckfrage; onAntwort: (ok: boolean) => void }) {
  const jaKnopf = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    // Wie beim Browser-Dialog: Enter bestaetigt, Esc bricht ab.
    jaKnopf.current?.focus()
    const aufTaste = (e: KeyboardEvent) => {
      if (e.key === "Escape") { e.stopPropagation(); onAntwort(false) }
    }
    // Capture: sonst schliesst Esc zuerst das Modal darunter.
    window.addEventListener("keydown", aufTaste, true)
    return () => window.removeEventListener("keydown", aufTaste, true)
  }, [onAntwort])

  return (
    <div
      className="pp-overlay"
      style={{
        // Ueber allen Anlege-Dialogen (bis 1100), unter den Toasts (10000).
        position: "fixed", inset: 0, zIndex: 2000,
        background: "rgba(0,0,0,0.45)",
        display: "flex", alignItems: "center", justifyContent: "center", padding: 16,
      }}
      onMouseDown={(e) => { if (e.target === e.currentTarget) onAntwort(false) }}
      role="alertdialog"
      aria-modal="true"
      aria-label={frage.titel}
    >
      <div style={{
        background: "#fff", borderRadius: 12, width: "100%", maxWidth: 460,
        boxShadow: "0 20px 60px rgba(0,0,0,.25)", overflow: "hidden",
      }}>
        <div style={{
          padding: "14px 20px", borderBottom: "0.5px solid rgba(44,44,42,0.1)",
          display: "flex", alignItems: "center", gap: 10, background: "#fffbeb",
        }}>
          <AlertTriangle size={16} color="#b45309" />
          <h3 style={{ margin: 0, fontSize: 15, fontWeight: 600 }}>{frage.titel}</h3>
        </div>

        <div style={{
          padding: 20, display: "flex", flexDirection: "column", gap: 10,
          fontSize: 13, lineHeight: 1.5, color: "#2C2C2A",
        }}>
          {frage.zeilen.map((z, i) => <p key={i} style={{ margin: 0 }}>{z}</p>)}
        </div>

        <div style={{
          padding: "12px 20px", borderTop: "0.5px solid rgba(44,44,42,0.1)",
          display: "flex", justifyContent: "flex-end", gap: 8,
        }}>
          <button type="button" onClick={() => onAntwort(false)}
            style={{
              background: "#fff", border: "0.5px solid rgba(44,44,42,0.2)",
              borderRadius: 7, padding: "7px 14px", fontSize: 13, cursor: "pointer",
            }}>
            {frage.nein || "Abbrechen"}
          </button>
          <button type="button" ref={jaKnopf} onClick={() => onAntwort(true)}
            style={{
              background: "#3C3489", color: "#fff", border: 0, borderRadius: 7,
              padding: "7px 16px", fontSize: 13, fontWeight: 600, cursor: "pointer",
            }}>
            {frage.ja}
          </button>
        </div>
      </div>
    </div>
  )
}
